import { NullQ } from "@pdfking/structs"

import type { Sequence } from "#public"

import { Zip } from "./Zip"

/**
 * Creates a record object from entries or from separate key and value sequences.
 *
 * @param entries - Iterable of [key, value] pairs
 * @returns Record object
 *
 * @example
 * ```ts
 * import { RecordCreate } from "@pdfking/tfunc"
 *
 * // From entries
 * RecordCreate([["name", "John"], ["age", 30]] as const)
 * // { name: "John", age: 30 }
 *
 * // From separate keys and values
 * RecordCreate(["name", "age", "city"], ["John", 30, "NYC"])
 * // { name: "John", age: 30, city: "NYC" }
 * ```
 */
export function RecordCreate<const E extends Readonly<Readonly<[PropertyKey, unknown]>[]>>(
  entries: E
): { [T in E[number] as T[0]]: T[1] }
export function RecordCreate<K extends PropertyKey, V>(
  entries: Sequence<Readonly<[K, V]>>
): { [P in K]: V }
export function RecordCreate<const K extends Readonly<PropertyKey[]>, T>(
  keys: K,
  values: Sequence<T>
): { [P in K[number]]: T }
export function RecordCreate<K extends PropertyKey, T>(
  keys: Sequence<K>,
  values: Sequence<T>
): { [P in K]: T }
export function RecordCreate<T>(
  keysOrEntries: Sequence<string> | Sequence<Readonly<[PropertyKey, T]>>,
  values?: Sequence<T>
): Record<string, T> {
  if (NullQ(values)) {
    return Object.fromEntries(keysOrEntries as Sequence<Readonly<[string, T]>>)
  }
  return Object.fromEntries(Zip(keysOrEntries as Sequence<string>, values))
}
